/**
 * The card grammar every conflict card in the Compare modal shares
 * (second design pass, operator hand-testing 2026-09-08).
 *
 * ONE CARD PER IMPORT ROW. Left = the row from your list: ONE entry, larger,
 * the constant. Right = what Thrive holds for that identity: N entries, compact
 * rows. The right column's header states the SIDE and the COUNT, so "which side
 * am I looking at" is never inferred from position alone.
 *
 * **N is not always 1 or 2.** The layout had to hold for 5, which is why the
 * right side is a list of ROWS rather than a second full panel: two fit without
 * scrolling and the list scrolls inside its own box past that, so a card with
 * five duplicates is the same height as a card with one and the queue below it
 * does not move.
 *
 * The differing AVATARS are the evidence. Two visibly different faces means
 * "different people, one URL is wrong"; the same face means "Thrive holds a
 * duplicate to merge". Nothing in this product can tell those apart — the
 * recruiter looking at the photos can, which is why the photos are the one
 * thing every card kind renders identically.
 *
 * ONE VERDICT LINE, everything longer behind the ⓘ. See the note above
 * `COMPANY_CHANGE_SECTION` in `conflictActionsCopy.ts` for why.
 *
 * The two card KINDS must not look alike, and the difference is carried by the
 * footer rather than by colour: a `resolvable` card offers controls that change
 * something, an `external` one says plainly where the fix lives and gives the
 * shortest route there. A disabled button, or no footer at all, both read as
 * "broken" — which is what the W1 card used to read as, its "fix these in
 * Thrive first" living in a shared section header that did not travel with the
 * card.
 */

import { Avatar } from "@nu-assist/ui";
import type { ReactNode } from "react";
import { thriveContactUrl } from "../../lib/thriveLinks";
import { InfoHint } from "../../routes/research/import-detail/InfoHint";
import { FROM_YOUR_LIST_HEADER, inThriveHeader } from "./conflictActionsCopy";

/** One entry on the right: what Thrive holds under this identity.
 *
 *  `name` / `company` / `title` are optional because one path genuinely cannot
 *  supply them: a registry decision stores an id and nothing else, and
 *  resolving names for it would cost a Thrive call per pair on a poll that runs
 *  every few seconds. Such a row shows the id and the deep link, which is the
 *  honest amount. */
export interface ThriveSideEntry {
  thriveId: string | null;
  name?: string | null;
  company?: string | null;
  title?: string | null;
  avatarUrl?: string | null;
  /** A second line under the entry — the registry attribution on a Different
   *  people card. Kept as a slot rather than a field so the shell never learns
   *  what a registry decision is. */
  note?: string | null;
}

/** One Thrive row: avatar, name, id, deep link.
 *
 *  `avatarSize` is the caller's, not this row's, because it depends on
 *  something a single row cannot see — HOW MANY rows there are. See
 *  `thriveAvatarSize`. */
function ThriveEntryRow({
  entry,
  testId,
  avatarSize,
}: {
  entry: ThriveSideEntry;
  testId: string;
  avatarSize: "sm" | "lg";
}) {
  const name = entry.name?.trim();
  const detail = [entry.company, entry.title].filter(Boolean).join(" · ");
  return (
    <li
      data-testid={testId}
      className="flex items-start gap-2 border-b border-border px-2.5 py-2 last:border-b-0"
    >
      {/* `name` is deliberately NOT passed to Avatar: initials would be drawn
          from whichever name we happen to hold, and printing the IMPORT row's
          initials beside a Thrive id would assert the two are the same person —
          the exact claim these cards exist to question. */}
      <Avatar src={entry.avatarUrl ?? undefined} size={avatarSize} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span className="text-body text-text">{name || "No name on file"}</span>
          {entry.thriveId && (
            <a
              data-testid={`${testId}-link`}
              href={thriveContactUrl(entry.thriveId)}
              target="_blank"
              rel="noreferrer"
              className="text-caption text-accent hover:underline"
            >
              #{entry.thriveId} →
            </a>
          )}
        </div>
        {detail && <p className="text-caption text-text-secondary">{detail}</p>}
        {entry.note && <p className="text-caption text-text-muted">{entry.note}</p>}
      </div>
    </li>
  );
}

export interface ConflictCardShellProps {
  /** DOM id — the dup-warning banner anchors at `#conflict-card-<id>`. */
  anchorId: string;
  /** Testid for the card itself; each kind keeps its own. */
  testId: string;
  /** LEFT: the import row. */
  source: {
    name: string;
    company?: string | null;
    title?: string | null;
    linkedinUrl?: string | null;
    avatarUrl?: string | null;
  };
  /** RIGHT: what Thrive holds. Empty renders no right column at all — a card
   *  with nothing on the right is asking a different question, and an empty box
   *  headed "In Thrive (0)" answers it wrongly. */
  thrive: ThriveSideEntry[];
  /** The ONE line the recruiter reads while scanning. */
  verdict: string;
  /** Everything longer, behind the ⓘ. Omit for a card that needs no expansion.
   *
   *  A `ReactNode` because one of these notes is structured — the "Two records,
   *  one LinkedIn URL" note has a heading, a `To fix it:` step and a closing
   *  line, and flattening it into one paragraph is exactly the wall of text the
   *  ⓘ exists to prevent. */
  verdictDetail?: ReactNode;
  /** The SAME note as plain text, for the screen reader. Required whenever
   *  `verdictDetail` is not already a string: the sighted surface is a tooltip
   *  and the assistive one is an `aria-describedby` target, and only a string
   *  can be the second. Defaulted from `verdictDetail` when it is one, so the
   *  common case stays a single prop. */
  verdictDetailText?: string;
  /** Leading glyph for the verdict line — the card KIND at a glance. */
  verdictTone?: "warning" | "conflict" | "resolved";
  /** Footer: the controls, or the route out. */
  footer?: ReactNode;
  /** A row above everything (the ignored pill + its date). */
  banner?: ReactNode;
  /** Dims the card without hiding it. */
  dimmed?: boolean;
  /**
   * Records that the last check came back "no". THE MOTION IS NOT HERE.
   *
   * It moved out on 2026-09-10, twice, and the second move is the one to keep:
   * the wobble now runs on the OPTION the recruiter chose, inside
   * `HighlightOptionGroup`, on the macOS password reading — the answer you gave
   * is what is being rejected, so the answer is what moves. The card kept
   * trying to carry it and could not: shaking a whole card (or one of its
   * columns) also moves the half that is fine, and the CSS-class mechanism it
   * used could not replay for a repeated rejection without a remount that
   * read as a blink.
   *
   * What survives here is the FACT, exposed as `data-shake` — several tests and
   * the reduced-motion assertion read it, and it is the honest thing for the
   * shell to own: the shell knows the verdict, the control knows how to react.
   *
   * `prefers-reduced-motion` is honoured in `src/index.css`, which neutralises
   * `.animate-shake` globally — deliberately there rather than conditionally
   * here, so no caller can forget it. The MESSAGE still lands without the
   * motion: this shake always ships paired with a toast.
   *
   * IT IS NOT AN ERROR AFFORDANCE. Reserve it for "we checked and the answer is
   * no". A check that could not RUN must not shake — that shakes the card at a
   * recruiter who did nothing wrong.
   */
  shake?: boolean;
  /**
   * A slot above the footer for a reward that belongs to an OUTCOME.
   *
   * A slot rather than a flag because the shell must not learn what is being
   * celebrated or which flag reveals it. The one caller today renders
   * `CleanupCelebration` here, from a re-check response's `cleared` branch and
   * only when `?gamification=off` is active.
   */
  celebration?: ReactNode;
}

const VERDICT_TONE_INK: Record<NonNullable<ConflictCardShellProps["verdictTone"]>, string> = {
  warning: "text-warning-ink",
  conflict: "text-conflict",
  resolved: "text-success-ink",
};

const VERDICT_TONE_GLYPH: Record<NonNullable<ConflictCardShellProps["verdictTone"]>, string> = {
  warning: "⚠",
  conflict: "⚠",
  resolved: "✓",
};

/** Thrive-side avatar size: MATCHES THE IMPORT SIDE when there is exactly one.
 *
 * The right column's `sm` exists for the W1 cards, where four contacts share a
 * URL and four large portraits would push the verdict line off the fold. It was
 * never a statement that Thrive's face matters less — but on the common card,
 * where the right side holds ONE contact, that is exactly what it looked like:
 * a big photo beside a small one reads as primary-and-thumbnail.
 *
 * Which is backwards for this surface. A one-to-one card asks the recruiter to
 * compare two faces and decide whether they are the same human, and that
 * comparison is hardest at the size where it matters least. Equal size is the
 * honest presentation of a question with two equal sides.
 *
 * Threshold is 1, not 2: at two entries the right column is already a LIST, and
 * a list of large portraits is the layout problem `sm` was introduced to solve.
 */
export function thriveAvatarSize(entryCount: number): "sm" | "lg" {
  return entryCount === 1 ? "lg" : "sm";
}

/** Beyond this many Thrive entries the right-hand list scrolls inside its own
 *  box. Two must fit without scrolling (the common W1 shape); three is the
 *  point where a fourth would start pushing the next card off the screen. */
const THRIVE_LIST_SCROLL_AFTER = 3;

export function ConflictCardShell({
  anchorId,
  testId,
  source,
  thrive,
  verdict,
  verdictDetail,
  verdictDetailText,
  verdictTone = "warning",
  footer,
  banner,
  dimmed = false,
  shake = false,
  celebration,
}: ConflictCardShellProps) {
  const scrolls = thrive.length > THRIVE_LIST_SCROLL_AFTER;
  const detailText =
    verdictDetailText ?? (typeof verdictDetail === "string" ? verdictDetail : undefined);
  return (
    <li
      data-testid={testId}
      id={`conflict-card-${anchorId}`}
      // `data-shake` is the TESTABLE half. The class alone is asserted through
      // a stylesheet that vitest does not load, and `prefers-reduced-motion`
      // strips the animation in `index.css` — so a test asserting the class
      // would be asserting the wrong thing in the one environment that matters.
      // The attribute says "the card was told to shake"; the class says "and
      // here is the motion, if this reader wants motion".
      data-shake={shake ? "true" : undefined}
      className={`flex flex-col gap-3 rounded-md border border-border bg-surface p-4${
        dimmed ? " opacity-60" : ""
      }`}
    >
      {banner}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {/* LEFT — your row. One, larger, the constant. */}
        <div data-testid={`${testId}-source`} className="rounded-sm border border-border bg-bg">
          <p className="border-b border-border px-2.5 py-1.5 text-label font-semibold text-text-secondary">
            {FROM_YOUR_LIST_HEADER}
          </p>
          <div className="flex items-start gap-3 p-2.5">
            <Avatar src={source.avatarUrl ?? undefined} name={source.name} size="lg" />
            <div className="min-w-0 flex-1">
              <p className="text-body font-medium text-text">{source.name}</p>
              {source.title && <p className="text-caption text-text-secondary">{source.title}</p>}
              {source.company && (
                <p className="text-caption text-text-secondary">{source.company}</p>
              )}
              {source.linkedinUrl && (
                <a
                  href={source.linkedinUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-1 inline-block text-caption text-accent hover:underline"
                >
                  LinkedIn →
                </a>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT — what Thrive holds. Headed with its own count. */}
        {thrive.length > 0 && (
          <div data-testid={`${testId}-thrive`} className="rounded-sm border border-border bg-bg">
            <p className="border-b border-border px-2.5 py-1.5 text-label font-semibold text-text-secondary">
              {inThriveHeader(thrive.length)}
            </p>
            {/* Scrolls INSIDE its own box past three entries, so a card with
                five duplicates is the same height as a card with one. */}
            <ul className={scrolls ? "max-h-48 overflow-y-auto" : undefined}>
              {thrive.map((entry, index) => (
                <ThriveEntryRow
                  key={entry.thriveId ?? `entry-${index}`}
                  entry={entry}
                  testId={`${testId}-thrive-row`}
                  avatarSize={thriveAvatarSize(thrive.length)}
                />
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* ONE verdict line. The ⓘ carries the rest. */}
      <div className="flex items-start gap-1.5">
        <p
          data-testid={`${testId}-verdict`}
          className={`text-body ${VERDICT_TONE_INK[verdictTone]}`}
          // The detail is a tooltip for a sighted reader; a screen reader gets
          // it here, on the sentence it explains. `InfoHint`'s own docstring
          // makes this the caller's job for exactly this reason — its button
          // carries `aria-describedby` on itself, so a reader on the text never
          // hears the note.
          aria-describedby={detailText ? `${anchorId}-verdict-detail` : undefined}
        >
          <span aria-hidden="true">{VERDICT_TONE_GLYPH[verdictTone]} </span>
          {verdict}
        </p>
        {verdictDetail && (
          <>
            {detailText && (
              <span id={`${anchorId}-verdict-detail`} className="sr-only">
                {detailText}
              </span>
            )}
            {/* `escapesOverflow`: every conflict card renders inside the
                modal body's `overflow-y-auto`, and the cards below the fold are
                exactly the ones whose bubble would be cut at its edge. The
                clipping the operator reported was in the contacts table; this
                surface has the same container and would have produced it next. */}
            <InfoHint content={verdictDetail} label="Why this row is flagged" escapesOverflow />
          </>
        )}
      </div>

      {/* ABOVE the footer, not inside it: the footer is where the controls
          live, and a reward sitting among the controls reads as one. */}
      {celebration}

      {footer}
    </li>
  );
}
